<section class="guide-shell">
  <article class="guide-card">
    <header class="guide-header">
      <h1>Performance Analysis Guide</h1>
      <p>
        This guide explains the concepts used by the charts, how values are computed,
        and how to interpret each visualization in practical optimization workflows.
      </p>
    </header>

    <section>
      <h2>1. Dashboard Purpose</h2>
      <p>
        The viewer combines region-aware timing data with scalability indicators so you can identify
        where parallel execution is effective and where it breaks down.
      </p>
      <ul>
        <li><strong>Heatmaps</strong> show how a metric changes across workloads and parallel configurations.</li>
        <li><strong>Region comparison</strong> shows which code areas are responsible for poor scaling.</li>
        <li><strong>Topbar region metrics</strong> summarize impact and stability signals for selected regions.</li>
      </ul>
    </section>

    <section>
      <h2>2. Data Model, Execution Models, and Axes</h2>
      <p>
        For each selected region, the system builds matrices where each cell represents one
        <code>(workload, parallel configuration)</code> combination.
      </p>
      <ul>
        <li>The X axis uses workloads (<code>i1, i2, i3, ...</code>).</li>
        <li>The Y axis uses parallel configurations, but the exact meaning depends on the execution model.</li>
        <li>Cell values are medians of repeated runs for robust behavior under noise.</li>
      </ul>
      <div class="formula" [innerHTML]="formulas.median"></div>

      <h3>MPI</h3>
      <p>
        In MPI-only runs, each row corresponds to an MPI rank count. A row labeled <code>8</code> means
        execution with eight MPI ranks.
      </p>

      <h3>OpenMP</h3>
      <p>
        In OpenMP-only runs, each row corresponds to the core or thread count used by the execution.
      </p>

      <h3>Hybrid (MPI + OpenMP)</h3>
      <p>
        In hybrid runs, one execution combines MPI ranks and OpenMP cores per rank. A topology such as
        <code>2x4</code> means two MPI ranks with four OpenMP cores per rank, for a total of eight cores.
      </p>
      <ul>
        <li><strong>Heatmap view</strong> groups rows by total core count, so <code>2x4</code> and <code>4x2</code> may share the same row.</li>
        <li><strong>Topology view</strong> keeps each MPI x OpenMP topology as a separate row, even when the total core count is the same.</li>
      </ul>

      <div class="callout callout--tip">
        <h3>View Selection Tip</h3>
        <p>
          Use <code>Heatmap view</code> when you want a smoother global reading of scaling by effective parallelism.
          Use <code>Topology view</code> when you want to compare how different MPI/OpenMP decompositions behave at the same total core count.
        </p>
      </div>
    </section>

    <section>
      <h2>3. Core Metrics Used by Charts</h2>

      <h3>Parallel Efficiency</h3>
      <div class="formula" [innerHTML]="formulas.efficiency"></div>
      <p>
        Where <code>T_seq(w)</code> is the runtime at the sequential baseline for workload <code>w</code>,
        and <code>T_par(w,p)</code> is runtime at parallel configuration <code>p</code>. Here, <code>p</code> means
        the effective parallelism shown on the Y axis: MPI ranks for MPI runs, cores for OpenMP runs, and total cores
        in hybrid <code>Heatmap view</code>. In <code>Topology view</code>, two rows may have the same effective
        parallelism but different MPI/OpenMP decompositions. Efficiency values are displayed as percentage (<code>100 * E</code>).
      </p>
      <ul>
        <li>High values indicate efficient parallel usage.</li>
        <li>Low values indicate overhead, synchronization, or imbalance.</li>
      </ul>

      <h3>Speedup</h3>
      <div class="formula" [innerHTML]="formulas.speedup"></div>
      <p>
        Speedup measures acceleration relative to the sequential baseline row, which corresponds to effective
        parallelism <code>1</code> (for example, one MPI rank, one OpenMP core, or hybrid <code>1x1</code>).
        The ideal trend is <span [innerHTML]="formulas.idealSpeedup"></span>, but real systems usually stay below this line.
      </p>
      <div class="callout callout--warn">
        <h3>Sequential baseline required (1 core)</h3>
        <p>Speedup and efficiency are computed against <code>T_seq</code> (parallelism == 1). Files without a 1-core run block all charts.</p>
        <ul>
          <li><strong>OpenMP:</strong> <code>pascalanalyzer ./main -c 1,2,4 ...</code></li>
          <li><strong>MPI:</strong> <code>pascalanalyzer ./run --ranks 1,2,4 ...</code></li>
          <li><strong>Hybrid:</strong> <code>pascalanalyzer ./run --ranks 1,2 -c 1,4 ...</code> (must include <code>1x1</code>)</li>
        </ul>
      </div>

      <h3>Load Imbalance Heatmap</h3>
      <p>
        Imbalance values come from analyzer data and are aggregated with median per cell. Higher values mean
        worker execution is less evenly distributed, which usually reduces efficiency and speedup.
      </p>
    </section>

    <section>
      <h2>4. Scalability Views</h2>
      <p>
        Scalability charts are built from the efficiency matrix and emphasize change rather than absolute value.
      </p>

      <h3>Strong Scalability (parallel-configuration baseline)</h3>
      <div class="formula" [innerHTML]="formulas.strong"></div>
      <p>
        Compares each parallel configuration against the first non-sequential configuration for the same workload.
        Positive values mean improvement over baseline; negative values mean regression. In hybrid analysis,
        the interpretation depends on the active view: aggregated by total cores in <code>Heatmap view</code>,
        or by explicit topology in <code>Topology view</code>.
      </p>

      <h3>Scalability Across Workloads</h3>
      <div class="formula" [innerHTML]="formulas.scalab"></div>
      <p>
        Compares each workload against the first workload at the same parallel configuration.
        It shows sensitivity to input growth.
      </p>

      <h3>Weak Scalability</h3>
      <div class="formula" [innerHTML]="formulas.weak"></div>
      <p>
        Compares diagonally matching workload/thread growth steps. This captures how efficiency evolves when
        both problem size and parallel resources increase together.
      </p>

      <h3>Relative Scalability Variants</h3>
      <div class="formula" [innerHTML]="formulas.relativeStrong"></div>
      <div class="formula" [innerHTML]="formulas.relativeScalab"></div>
      <div class="formula" [innerHTML]="formulas.relativeWeak"></div>
      <p>
        Relative variants compare each point with its nearest neighbor instead of the first baseline.
        They are useful to detect local degradation steps that global baselines can hide.
      </p>
    </section>

    <section>
      <h2>5. Region Tree and Selection Logic</h2>
      <p>
        Regions are hierarchical (for example, <code>0</code>, <code>0.1</code>, <code>0.1.2</code>),
        where parent nodes represent broader scopes and children represent more specific code blocks.
      </p>
      <ul>
        <li>Selecting one region focuses all charts on that region only.</li>
        <li>Selecting multiple regions enables side-by-side comparison per metric.</li>
        <li>Region 0 is the whole-program baseline.</li>
      </ul>
    </section>

    <section>
      <h2>6. Region Metrics and Their Impact</h2>
      <p>
        The topbar metrics help prioritize optimization targets before deep-diving into all heatmaps.
      </p>

      <h3>Code Lines</h3>
      <p>
        This is the source line interval associated with the selected region.
      </p>
      <ul>
        <li>Impact: fast navigation to the exact code scope.</li>
        <li>Interpretation: combine with high impact metrics to decide where to profile first.</li>
      </ul>

      <h3>% Exec (Execution Share Range)</h3>
      <div class="formula" [innerHTML]="formulas.execPct"></div>
      <p>
        For each configuration, it measures how much of the parent execution time is spent in the region.
        The topbar shows the min-max range across all sampled configurations.
      </p>
      <ul>
        <li>Expected range: <code>0%</code> to <code>100%</code>.</li>
        <li>Impact: consistently high values indicate globally relevant regions.</li>
        <li>Interpretation cue: a region with high % Exec and poor efficiency is often a first optimization candidate.</li>
      </ul>

      <h3>Delta T (Time Variation Range)</h3>
      <div class="formula" [innerHTML]="formulas.deltaT"></div>
      <p>
        This metric is derived from imbalance and converted to percentage. The topbar range uses median
        variation per configuration and then reports min-max across configurations.
      </p>
      <ul>
        <li>Expected range: typically <code>0%</code> to <code>100%</code> (may vary with input data quality).</li>
        <li>Impact: higher values signal instability or uneven work distribution across runs/workers.</li>
        <li>Interpretation cue: high Delta T with low efficiency usually indicates synchronization or partitioning problems.</li>
      </ul>

      <h3>Arguments (i1, i2, ...)</h3>
      <p>
        Input argument labels identify workloads. They provide context to map each row (<code>i1</code>, <code>i2</code>, ...)
        to concrete problem sizes or scenarios.
      </p>
      <ul>
        <li>Impact: prevents wrong cross-row comparisons.</li>
        <li>Interpretation cue: always compare similar argument semantics when evaluating scaling quality.</li>
      </ul>
    </section>

    <section>
      <h2>7. How to Interpret the Charts in Practice</h2>
      <ol>
        <li>Start with % Exec: find regions that consume substantial runtime share.</li>
        <li>Check which execution model is active: MPI, OpenMP, or Hybrid.</li>
        <li>If the run is hybrid, choose <code>Heatmap view</code> for a total-core overview or <code>Topology view</code> for decomposition-sensitive analysis.</li>
        <li>Check efficiency heatmap: locate where efficiency drops as the parallel configuration grows.</li>
        <li>Check Delta T and imbalance: confirm whether instability or uneven work explains the drop.</li>
        <li>Use speedup to estimate practical gains already achieved.</li>
        <li>Use scalability and relative views to identify exactly where growth stops paying off.</li>
        <li>Compare sibling regions to decide whether to optimize algorithm, data partitioning, or synchronization first.</li>
      </ol>

      <div class="callout callout--tip">
        <h3>Interpretation Tip</h3>
        <p>
          Prioritize regions with both high <code>% Exec</code> and poor scaling signals.
          Improving a low-impact region rarely changes end-to-end runtime.
        </p>
      </div>

      <div class="callout callout--warn">
        <h3>Warning</h3>
        <p>
          Do not confuse efficiency with speedup. A region can show acceptable speedup at small parallel configurations
          while still having poor efficiency trends at larger scales.
        </p>
      </div>
    </section>
  </article>
</section>
